import { SimpleClusterForm, DynamicSupplyHardware } from "@/types";

import { RecipeConfig } from "./RecipesSection.constants";

const ENV_FIELD_PREFIXES = [
  "env_normal_key_",
  "env_normal_value_",
  "env_private_key_",
  "env_private_value_"
];

const clearEnvVariables = (values: SimpleClusterForm) => {
  Object.keys(values).forEach((key) => {
    if (ENV_FIELD_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      delete (values as Record<string, unknown>)[key];
    }
  });
};

const clearHealthCheckFields = (values: SimpleClusterForm) => {
  values.healthCheckPath = "";
  delete values.healthCheckIntervalSeconds;
  delete values.healthCheckTimeoutSeconds;
  delete values.healthCheckGracePeriodSeconds;
};

const applyBasicRecipeFields = (values: SimpleClusterForm, recipe: RecipeConfig) => {
  if (recipe.registry_config?.image_url) {
    values.containerImage = recipe.registry_config.image_url;
  }
  if (recipe.container_config?.replica_count) {
    values.replicaCount = String(recipe.container_config.replica_count);
  }
  if (recipe.registry_config?.registry_username) {
    values.username = recipe.registry_config.registry_username;
  }
  if (recipe.registry_config?.registry_secret) {
    values.secret = recipe.registry_config.registry_secret;
  }
  if (recipe.container_config?.traffic_port) {
    values.trafficPort = recipe.container_config.traffic_port;
  }
  values.healthCheckPath = recipe.healthCheckPath ?? "/health";
  values.healthCheckIntervalSeconds = 10;
  values.healthCheckTimeoutSeconds = 10;
  values.healthCheckGracePeriodSeconds = 60;
};

export const clearRecipeFields = (values: SimpleClusterForm) => {
  clearEnvVariables(values);

  values.containerImage = "";

  delete values.durationQuantity;
  values.username = "";
  values.secret = "";
  delete values.trafficPort;
  delete values.command;
  clearHealthCheckFields(values);

  // We do NOT clear hardware fields as the user might have selected them manually.
};

export const findHardwareMatch = (
  hardwareList: DynamicSupplyHardware[] | undefined,
  hardwareName?: string
): DynamicSupplyHardware | undefined => {
  if (!hardwareList || !hardwareName) return undefined;

  return hardwareList.find((h) =>
    h.hardwareName.toLowerCase().includes(hardwareName.toLowerCase())
  );
};

export const hardwareMeetsRecipeVram = (
  hardware: DynamicSupplyHardware,
  recipe: RecipeConfig
): boolean => {
  const minTotalVram = getRecipeMinTotalVramGb(recipe);
  if (minTotalVram == null) return true;
  const hardwareTotalVram = hardware.vramPerCard * (hardware.numCards ?? 1);
  return hardwareTotalVram >= minTotalVram;
};

/** Min total VRAM across cards (GB): per-card floor × required GPU count when both are set. */
export const getRecipeMinTotalVramGb = (recipe: RecipeConfig): number | undefined => {
  if (recipe.min_vram_per_card == null) return undefined;
  return recipe.min_vram_per_card * (recipe.gpus_per_container ?? 1);
};

/** Prefer a name match that also meets total VRAM; otherwise first eligible option. */
export const findHardwareMatchForRecipe = (
  hardwareList: DynamicSupplyHardware[] | undefined,
  recipe: RecipeConfig
): DynamicSupplyHardware | undefined => {
  if (!hardwareList?.length) return undefined;

  const eligible = getHardwareMatchingRecipe(hardwareList, recipe);

  if (recipe.hardware_name) {
    const named = eligible.find((h) =>
      h.hardwareName.toLowerCase().includes(recipe.hardware_name!.toLowerCase())
    );
    if (named) return named;
  }

  return eligible[0];
};

/** Available (not sold-out) hardware rows that meet the recipe's VRAM gate. */
export const getHardwareMatchingRecipe = (
  hardwareList: DynamicSupplyHardware[] | undefined,
  recipe: RecipeConfig
): DynamicSupplyHardware[] => {
  if (!hardwareList?.length) return [];
  return hardwareList.filter((h) => !h.isSoldOut && hardwareMeetsRecipeVram(h, recipe));
};

/**
 * Whether any available inventory can run the recipe.
 * If the list isn't loaded yet, treat as available so tiles aren't disabled prematurely.
 */
export const isRecipeAvailableInInventory = (
  hardwareList: DynamicSupplyHardware[] | undefined,
  recipe: RecipeConfig
): boolean => {
  if (!hardwareList?.length) return true;
  return getHardwareMatchingRecipe(hardwareList, recipe).length > 0;
};

/** True when VRAM-compatible hardware exists but every matching row is sold out. */
export const isRecipeSoldOutInInventory = (
  hardwareList: DynamicSupplyHardware[] | undefined,
  recipe: RecipeConfig
): boolean => {
  if (!hardwareList?.length) return false;
  if (getHardwareMatchingRecipe(hardwareList, recipe).length > 0) return false;
  return hardwareList.some((h) => h.isSoldOut && hardwareMeetsRecipeVram(h, recipe));
};

export const recipeAvailabilityLabel = (_recipe: RecipeConfig): string => {
  return "Compatible hardware sold out";
};

const applyHardwareSelection = (values: SimpleClusterForm, recipe: RecipeConfig) => {
  if (recipe.min_vram_per_card == null && !recipe.hardware_name) return;

  const hardwareList =
    (values.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
      ?.hardwareList || [];

  const currentDeploymentId = values.deploymentId;
  const currentHardwareId = values.hardware;

  const currentHardware = hardwareList.find(
    (h) =>
      (currentDeploymentId && String(h.deploymentId) === String(currentDeploymentId)) ||
      (currentHardwareId && String(h.hardwareId) === String(currentHardwareId))
  );

  if (currentHardware && hardwareMeetsRecipeVram(currentHardware, recipe)) {
    const nameOk =
      !recipe.hardware_name ||
      currentHardware.hardwareName.toLowerCase().includes(recipe.hardware_name.toLowerCase());
    if (nameOk) {
      return;
    }
  }

  const hardware = findHardwareMatchForRecipe(hardwareList, recipe);

  if (!hardware) return;

  const defaultQty = Math.max(
    1,
    recipe.gpus_per_container ?? hardware.numCards ?? 1
  );

  values.hardware = hardware.hardwareId;
  values.deploymentId = hardware.deploymentId;
  if (values.hardwareQty) {
    values.hardwareQty[hardware.hardwareId] = defaultQty;
  } else {
    values.hardwareQty = { [hardware.hardwareId]: defaultQty };
  }
  values.hardwareQuantity = defaultQty;
  values.locations = [`${hardware.location}`];
  values.supplier = hardware.supplier;
};

const buildCommandFromRecipe = (recipe: RecipeConfig) => {
  let cmdParts: string[] = [];

  if (recipe.container_config?.entrypoint) {
    cmdParts = [...cmdParts, ...recipe.container_config.entrypoint];
  }
  if (recipe.container_config?.args) {
    cmdParts = [...cmdParts, ...recipe.container_config.args];
  }

  return cmdParts.length > 0 ? JSON.stringify(cmdParts) : undefined;
};

const applyCommand = (values: SimpleClusterForm, recipe: RecipeConfig) => {
  const command = buildCommandFromRecipe(recipe);
  if (command) {
    values.command = command;
  }
};

const applyEnvVariables = (values: SimpleClusterForm, recipe: RecipeConfig) => {
  if (recipe.container_config?.env_variables) {
    Object.entries(recipe.container_config.env_variables).forEach(([k, v], idx) => {
      (values as Record<string, unknown>)[`env_normal_key_${idx}`] = k;
      (values as Record<string, unknown>)[`env_normal_value_${idx}`] = v;
    });
  }

  if (recipe.container_config?.secret_env_variables) {
    Object.entries(recipe.container_config.secret_env_variables).forEach(([k, v], idx) => {
      (values as Record<string, unknown>)[`env_private_key_${idx}`] = k;
      (values as Record<string, unknown>)[`env_private_value_${idx}`] = v;
    });
  }
};

export const applyRecipeToFormValues = (
  prevValues: SimpleClusterForm,
  recipe: RecipeConfig
): SimpleClusterForm => {
  const newValues = { ...prevValues };

  clearEnvVariables(newValues);
  applyBasicRecipeFields(newValues, recipe);
  applyHardwareSelection(newValues, recipe);
  applyCommand(newValues, recipe);
  applyEnvVariables(newValues, recipe);

  return newValues;
};
